import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../context/AuthContext'

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #f5f4ff;

  background-repeat: no-repeat;
  background-position: right center;
  background-size: cover;
  background-attachment: fixed;
  @media(max-width:768px){min-height:calc(100dvh - 56px);padding:1rem;background-attachment:scroll;}
`;

const LoginCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-sizing: border-box;
  @media(max-width:768px){padding:1.5rem;}
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InputGroup = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: calc(100% - 3.5rem);
  padding: 0.75rem 1rem;
  padding-left: 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 1rem;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #3b82f6;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
`;

const Button = styled.button`
  width: 100%;
  padding: 0.75rem;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #2563eb;
  }

  &:disabled {
    background-color: #94a3b8;
    cursor: not-allowed;
  }
`;

const LinkContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
  font-size: 0.875rem;
`;

const Link = styled.a`
  color: #3b82f6;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;




const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const LoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
  
    // 유효성 검사
    if (!email || !password) {
      setError('모든 필드를 입력해주세요');
      setLoading(false);
      return;
    }
  
    try {
      // fetch를 사용한 API 호출
      const response = await fetch('/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          user_pwd: password
        })
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        setError(response.status === 401 ? '아이디 또는 비밀번호가 일치하지 않습니다.'
          : response.status === 403 ? 'SkynetManager 접근 권한이 없습니다.'
          : '로그인 서버에 연결하지 못했습니다. 잠시 후 다시 시도해주세요.');
        return;
      }
  
      // 로그인 성공 - 응답 구조에 맞게 처리
      if (data.success) {
        // JWT 토큰 저장 (API 인증용)
        if (data.token) {
          localStorage.setItem('token', data.token);
          sessionStorage.setItem('token', data.token);
        }
        // 사용자 정보 저장
        login(data.user);
        console.log('로그인 성공');

        // 대시보드로 리디렉션
        navigate('/');
      } else {
        setError('아이디 또는 비밀번호가 일치하지 않습니다');
      }
    } catch (err) {
      console.error('로그인 오류:', err);
      setError('로그인 중 오류가 발생했습니다. 나중에 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer className="console-login">
      <div className="console-login-story"><a href="/" className="console-logo"><span className="console-logo-mark">S</span><span>Skynet<small>CLOUD CONSOLE</small></span></a><div><span className="console-eyebrow">YOUR CLOUD, CONNECTED.</span><h2>만들고, 검증하고,<br/>안정적으로 운영하세요.</h2><p>개발부터 운영까지.<br/>AntsNest의 서비스와 인프라를 한곳에서 관리합니다.</p><ol><li>01 <span>Ansible + PM2</span></li><li>02 <span>K3s</span></li><li>03 <span>Kubernetes</span></li></ol></div><small>ANTSNEST · SKYNET</small></div>
      <LoginCard className="console-login-card">
        <span className="console-eyebrow">WELCOME BACK</span><Title>워크스페이스 로그인</Title><p className="console-login-sub">AntsNest 계정으로 시작하세요.</p>
        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <InputIcon>
              <Mail size={18} />
            </InputIcon>
            <Input
              type="email"
              aria-label="이메일 주소"
              autoComplete="username"
              placeholder="이메일 주소"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </InputGroup>
          <InputGroup>
            <InputIcon>
              <Lock size={18} />
            </InputIcon>
            <Input
              type={showPassword ? 'text' : 'password'}
              aria-label="비밀번호"
              autoComplete="current-password"
              placeholder="비밀번호"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <PasswordToggle
              type="button"
              aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </PasswordToggle>
          </InputGroup>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          <Button type="submit" disabled={loading}>
            {loading ? '로그인 중...' : '로그인'}
          </Button>
        </Form>
        <LinkContainer>
          <Link href="/forgot-password">비밀번호 찾기</Link>
          <Link href="/signup">회원가입</Link>
        </LinkContainer>
      </LoginCard>
    </PageContainer>
  );
};

export default LoginPage;
